import { PROGRAMS, type MapleProgram } from '@/OS/utils/programs';

export type AgentToolCategory = 'os_app' | 'mcp_service';

export type OSTool = {
  id: string;
  category: 'os_app';
  name: string;
  iconName?: string;
  supportedFileTypes: string[];
  commonActions: string[];
  samplePrompts: string[];
};

export type MCPTool = {
  id: string;
  category: 'mcp_service';
  name: string;
  tier?: string;
  description?: string;
  samplePrompts: string[];
};

const OS_TOOL_OVERRIDES: Record<string, Partial<OSTool>> = {
  'file-explorer': {
    supportedFileTypes: ['*'],
    commonActions: ['browse', 'open', 'rename', 'delete'],
    samplePrompts: ['Show me what is in Documents\\Projects', 'Open the README in my project folder'],
  },
  notepad: {
    supportedFileTypes: ['txt', 'md', 'json', 'csv', 'log', 'ini', 'conf'],
    commonActions: ['open', 'edit', 'save'],
    samplePrompts: ['Open Notes.txt and add a new bullet', 'Create a changelog.md in my project folder'],
  },
  'maple-word': {
    supportedFileTypes: ['mword', 'md', 'html', 'txt'],
    commonActions: ['draft', 'summarize', 'format'],
    samplePrompts: ['Draft a one-page project brief', 'Summarize conversation.txt into an executive summary'],
  },
  'maple-sheet': {
    supportedFileTypes: ['csv', 'msheet', 'sheet'],
    commonActions: ['analyze', 'chart', 'clean'],
    samplePrompts: ['Open dataset.csv and compute monthly totals', 'Create a chart from this CSV'],
  },
  terminal: {
    supportedFileTypes: ['*'],
    commonActions: ['inspect', 'run', 'navigate'],
    samplePrompts: ['List the files in Documents\\Projects', 'Find all .md files under my project'],
  },
  'ai-task-manager': {
    supportedFileTypes: [],
    commonActions: ['monitor', 'simulate'],
    samplePrompts: ['Start an isolated task and show progress'],
  },
};

function iconNameOf(program: MapleProgram): string | undefined {
  // lucide-react components are functions with a name; good enough for display.
  return (program.icon as any)?.displayName || (program.icon as any)?.name;
}

export function getOSToolsCatalog(): OSTool[] {
  return PROGRAMS.map((p) => {
    const base: OSTool = {
      id: p.id,
      category: 'os_app',
      name: p.label,
      iconName: iconNameOf(p),
      supportedFileTypes: [],
      commonActions: [],
      samplePrompts: [],
    };

    const override = OS_TOOL_OVERRIDES[p.id] || {};
    return {
      ...base,
      supportedFileTypes: override.supportedFileTypes ?? base.supportedFileTypes,
      commonActions: override.commonActions ?? base.commonActions,
      samplePrompts: override.samplePrompts ?? base.samplePrompts,
    };
  });
}
